import LocationSearchInput from "@components/Signup/LocationSearchInput";
import { TextInput, TextInputType } from "@components/TextInput";
import { FullNameSection } from "@components/donate/DonateV3/PaymentProcess/components/FullNameSection";
import { DonateFormContext } from "@components/donate/DonateV3/PaymentProcess/useDonateFormContext";
import { validateUserEmail } from "@components/donate/DonateV3/PaymentProcess/validators";
import { DonateFormType } from "@components/donate/DonateV3/types";
import { COLLECT_ADDRESS, useCollectAddress } from "@components/donate/hooks";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { useContext, useLayoutEffect, useState } from "react";
import { Controller, UseFormReturn } from "react-hook-form";

import { Location } from "@every.org/common/src/codecs/location";

import { AuthContext } from "src/context/AuthContext";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { spacing } from "src/theme/spacing";
import { TextSize, textSizeCss } from "src/theme/text";

const StyledTextInput = styled(TextInput)`
  label {
    ${cssForMediaSize({
      min: MediaSize.MEDIUM,
      css: textSizeCss[TextSize.s],
    })}
  }
`;

export const DonorInfo = ({
  form,
  formContext,
}: {
  form: Omit<UseFormReturn<DonateFormType>, "handleSubmit">;
  formContext: DonateFormContext;
}) => {
  const {
    control,
    watch,
    formState: { errors },
  } = form;

  const email = watch("email");
  const firstName = watch("firstName");
  const lastName = watch("lastName");
  const authState = useContext(AuthContext);

  const userEmailError = errors.email;

  useLayoutEffect(() => {
    // #15705 reset payment sources on user info change
    // and force the user to attach a new one
    // to prevent the "source is attached to a different customer" error.
    // #17954 compare auth state user to form user so a valid source isn't removed on first load
    // prevents the "A bank account with that routing number and account number already exists for this customer." error.
    if (
      email !== authState.guestUser?.email ||
      firstName !== authState.guestUser?.firstName ||
      lastName !== authState.guestUser?.lastName
    ) {
      formContext.setPaymentSources([]);
      form.setValue("paymentSource", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, firstName, lastName]);

  return (
    <div
      css={css`
        > :not(:last-child) {
          margin-bottom: ${spacing.oldM};
        }
      `}
    >
      <FullNameSection control={control} form={form} />
      <Controller
        control={control}
        name="email"
        render={({ field: { onChange, value } }) => (
          <StyledTextInput
            type={TextInputType.EMAIL}
            labelText="Email"
            name="email"
            id="email"
            data-tname="email"
            autoComplete="email"
            value={value}
            onChange={onChange}
            onBlur={() =>
              validateUserEmail(value, form.setError, form.clearErrors)
            }
            validationStatus={
              userEmailError && {
                success: false,
                message: userEmailError.message,
              }
            }
            required
            collapseDescriptionSpace
          />
        )}
      />
      <AddressInput form={form} formContext={formContext} />
    </div>
  );
};

export const AddressInput = ({
  form,
  formContext,
}: {
  form: Omit<UseFormReturn<DonateFormType>, "handleSubmit">;
  formContext: DonateFormContext;
}) => {
  const {
    control,
    watch,
    formState: { errors },
  } = form;
  const location = watch("location");
  const [userInputLocation, setUserInputLocation] = useState(
    location?.address || ""
  );
  const userLocationError = errors.location;

  const { collectAddress, hasAddress } = useCollectAddress(formContext);
  if (collectAddress === COLLECT_ADDRESS.NO || hasAddress) {
    return null;
  }

  return (
    <Controller
      control={control}
      name="location"
      render={({ field: { onChange, value } }) => (
        <LocationSearchInput
          labelText={"Address"}
          labelTextCss={cssForMediaSize({
            min: MediaSize.MEDIUM,
            css: textSizeCss[TextSize.s],
          })}
          name="location"
          data-tname="location"
          placeholder=""
          value={userInputLocation}
          handleTextChange={setUserInputLocation}
          onLocationChanged={(newLocation?: Location) => {
            onChange(newLocation);
          }}
          addressOnly
          validationStatus={
            userLocationError && {
              success: false,
              message: userLocationError.message,
            }
          }
          collapseDescriptionSpace
        />
      )}
    />
  );
};
